// useIntlAccaDNA — per-nation DNA profile. The `nations` reference and
// `intl_acca_team_dna` tables are a future C-phase migration.
//
// DEFENSIVE / HONEST-EMPTY: those tables DO NOT EXIST yet. Each query
// is caught and degraded to null so the page renders its "Awaiting
// data" empty shell. The tournament table IS real. NEVER throw,
// NEVER fabricate.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import type { IntlAccaTournament } from "../db/intl-acca-types.ts";
import type {
  IntlAccaTeamDNA,
  Nation,
} from "../db/intl-acca-types-extended.ts";

export interface IntlAccaDNAVM {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  nation: Nation | null;
  dna: IntlAccaTeamDNA | null;
  refetch: () => Promise<void>;
}

export function useIntlAccaDNA(
  tournamentId: string,
  teamCode: string,
): IntlAccaDNAVM {
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const [s, setS] = useState<Omit<IntlAccaDNAVM, "refetch">>({
    loading: true, error: null, tournament: null, nation: null, dna: null,
  });

  const fetchAll = useCallback(async () => {
    if (!tournamentId || !teamCode) {
      setS({ loading: false, error: null, tournament: null, nation: null, dna: null });
      return;
    }
    setS((p) => ({ ...p, loading: true, error: null }));

    let tournament: IntlAccaTournament | null = null;
    try {
      const tRes = await supabase.from("intl_acca_tournaments")
        .select("*").eq("id", tournamentId).maybeSingle();
      if (tRes.error) throw tRes.error;
      tournament = (tRes.data as IntlAccaTournament | null) ?? null;
    } catch {
      tournament = null;
    }

    // Future tables — catch + degrade to null (page renders empty shell).
    let nation: Nation | null = null;
    try {
      const nRes = await supabase.from("nations")
        .select("*").eq("code", teamCode).maybeSingle();
      if (!nRes.error) nation = (nRes.data as Nation | null) ?? null;
    } catch {
      nation = null;
    }

    let dna: IntlAccaTeamDNA | null = null;
    try {
      const dRes = await supabase.from("intl_acca_team_dna")
        .select("*").eq("team", teamCode).eq("tournament_id", tournamentId)
        .maybeSingle();
      if (!dRes.error) dna = (dRes.data as IntlAccaTeamDNA | null) ?? null;
    } catch {
      dna = null;
    }

    setS({ loading: false, error: null, tournament, nation, dna });
  }, [supabase, tournamentId, teamCode]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
